import { t as uiText } from '../i18n';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { fetchBackups, BackupData } from '../api/backups';
import { functions } from '../lib/firebase';
import { useWorkspaceContext } from '../context/WorkspaceContext';
import { useBoardListContext } from '../context/BoardListContext';
import { useHashtagContext } from '../context/HashtagContext';
import { useIsWorkspaceAdminQuery } from '../features/auth/queries/workspaceRoleQueries';

interface BackupModalProps {
  onClose: () => void;
}

// 復元レスポンスの型定義（restoreBackupAll.tsのRestoreWorkspaceResponseと同期）
interface RestoreWorkspaceResponse {
  success: boolean;
  message: string;
  totalBoards: number;
  successfulRestores: number;
  failedRestores: number;
  restoredBoards: string[];
  totalFiles?: number;
  totalHashtags?: number;
  restoredFiles?: string[];
  restoredHashtags?: string[];
  preRestoreBackupId?: string;
  errors?: string[];
}

const BackupModal: React.FC<BackupModalProps> = ({ onClose }) => {
  const { workspaceId } = useParams();
  const { refreshFileStats } = useWorkspaceContext();
  const { refreshBoardList } = useBoardListContext();
  const { refreshHashtags } = useHashtagContext();
  const [backups, setBackups] = useState<BackupData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBackup, setSelectedBackup] = useState<BackupData | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState(0);
  const [restoreStatus, setRestoreStatus] = useState<string>('');
  
  // 管理者権限チェック（React Query版）
  const { data: isAdmin = false } = useIsWorkspaceAdminQuery(
    workspaceId ?? '',
    !!workspaceId
  );

  // バックアップ履歴を取得
  useEffect(() => {
    const loadBackups = async () => {
      if (!workspaceId) return;
      
      try {
        setLoading(true);
        setError(null);
        const backupList = await fetchBackups(workspaceId);
        setBackups(backupList);
      } catch (error) {
        console.error('バックアップ履歴の取得に失敗:', error);
        setError(uiText("Failed to load backup history"));
      } finally {
        setLoading(false);
      }
    };

    loadBackups();
  }, [workspaceId]);

  // 日付をフォーマット
  const formatDate = (date: any): string => {
    if (!date) return uiText("Unknown");
    
    let dateObj: Date;
    if (date.toDate && typeof date.toDate === 'function') {
      // Firestore Timestamp
      dateObj = date.toDate();
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      return uiText("Unknown");
    }
    
    return dateObj.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // 復元処理
  const handleRestore = async () => {
    if (!selectedBackup || !workspaceId) return;
    
    const confirmed = window.confirm(
      uiText("Restore everything from backup \"{{id}}\"?\n\n", { id: selectedBackup.id }) +
      uiText("※ This will overwrite current data.\n") +
      uiText("※ Data created after the restore point will not remain.\n") +
      uiText("※ This can take a while when there are many pages.\n") +
      uiText("※ A new backup is saved when you run this.\n") +
      uiText("※ Do not close the browser while restoring.")

    );
    
    if (!confirmed) return;
    
    try {
      setIsRestoring(true);
      setError(null);
      setRestoreProgress(0);
      setRestoreStatus(uiText("Starting restore..."));

      // 進捗表示のシミュレーション
      const progressInterval = setInterval(() => {
        setRestoreProgress(prev => {
          if (prev >= 90) return prev; // 90%で止める（完了時に100%にする）
          return prev + Math.random() * 10;
        });
      }, 500);

      // ステータス更新のシミュレーション
      const statusUpdates = [
        uiText("Creating a backup before restore..."),
        uiText("Deleting existing data..."),
        uiText("Checking backup file..."),
        uiText("Restoring page data..."),
        uiText("Restoring file data..."),
        uiText("Restoring hashtags..."),
        uiText("Finishing restore...")
      ];

      let statusIndex = 0;
      const statusInterval = setInterval(() => {
        if (statusIndex < statusUpdates.length) {
          setRestoreStatus(statusUpdates[statusIndex]);
          statusIndex++;
        }
      }, 2000);

      try {
        // Firebase Functions呼び出し
        const restoreBackupAll = httpsCallable(functions, 'restoreBackupAllFn');
        const result = await restoreBackupAll({
          workspaceId: workspaceId,
          backupId: selectedBackup.id,
          createPreRestoreBackup: true
        });
        
        // 進捗とステータス更新を停止
        clearInterval(progressInterval);
        clearInterval(statusInterval);
        setRestoreProgress(100);
        setRestoreStatus(uiText("Restore complete!"));
        
        const responseData = result.data as RestoreWorkspaceResponse;
      
      if (responseData.success) {
        // 成功時の詳細メッセージ
        let successMessage = uiText("Restore complete!\n\n");
        successMessage += uiText("Restore results:\n");
        successMessage += uiText("• Pages: {{successfulRestores}}/{{totalBoards}} items\n", { successfulRestores: responseData.successfulRestores, totalBoards: responseData.totalBoards });
        
        if (responseData.totalFiles !== undefined) {
          successMessage += uiText("• Files: {{v0}}/{{totalFiles}} items\n", { v0: responseData.restoredFiles?.length || 0, totalFiles: responseData.totalFiles });
        }
        
        if (responseData.totalHashtags !== undefined) {
          successMessage += uiText("• Hashtags: {{v0}}/{{totalHashtags}} items\n", { v0: responseData.restoredHashtags?.length || 0, totalHashtags: responseData.totalHashtags });
        }
        
        if (responseData.preRestoreBackupId) {
          successMessage += uiText("\nPre-restore backup ID: {{preRestoreBackupId}}", { preRestoreBackupId: responseData.preRestoreBackupId });
        }
        
        alert(successMessage);
        setSelectedBackup(null);
        
        // 復元完了後の画面更新処理
        try {
          setRestoreStatus(uiText("Refreshing the screen..."));
          
          // 並列で各種データを更新
          await Promise.all([
            // バックアップ一覧を再取得（新しい復元前バックアップが追加されている可能性）
            fetchBackups(workspaceId).then(backupList => setBackups(backupList)),
            // ボード一覧を更新
            refreshBoardList(),
            // ハッシュタグを更新
            refreshHashtags(),
            // ファイル統計を更新
            refreshFileStats()
          ]);
          
          setRestoreStatus(uiText("Screen refresh complete!"));
        } catch (refreshError) {
          console.warn('画面更新中にエラーが発生:', refreshError);
          setRestoreStatus(uiText("An error occurred while refreshing the screen, but restoration is complete"));
        }
        
      } else {
        // 部分的成功または失敗
        let errorMessage = uiText("❌ Restore failed\n\n");
        errorMessage += responseData.message || uiText("Details unknown");
        
        if (responseData.errors && responseData.errors.length > 0) {
          errorMessage += uiText("\n\n🔍 Error details:\n");
          errorMessage += responseData.errors.slice(0, 3).join('\n');
          if (responseData.errors.length > 3) {
            errorMessage += uiText("\n... {{v0}} more items", { v0: responseData.errors.length - 3 });
          }
        }
        
        setError(errorMessage);
      }
      } catch (innerError) {
        // 内部エラー（Firebase Functions呼び出し失敗）
        clearInterval(progressInterval);
        clearInterval(statusInterval);
        throw innerError;
      }
    } catch (error: any) {
      console.error('復元処理に失敗:', error);
      
      // 進捗表示をリセット
      setRestoreProgress(0);
      setRestoreStatus('');
      
      let errorMessage = uiText("Restore failed");
      
      // Firebase Functionsからのエラーメッセージを詳細に処理
      if (error.code === 'functions/unauthenticated') {
        errorMessage = uiText("Login required. Please log in again and try again.");
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = uiText("Only the owner can restore.");
      } else if (error.code === 'functions/not-found') {
        errorMessage = uiText("The specified backup was not found.");
      } else if (error.code === 'functions/invalid-argument') {
        errorMessage = uiText("Backup ID or Workspace ID is invalid.");
      } else if (error.code === 'functions/internal') {
        errorMessage = uiText("An internal server error occurred. Please try again later.");
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      
    } finally {
      setIsRestoring(false);
      // 進捗表示を少し遅らせてリセット（ユーザーが結果を確認できるように）
      setTimeout(() => {
        setRestoreProgress(0);
        setRestoreStatus('');
      }, 3000);
    }
  };

  const handleBack = () => {
    if (selectedBackup) {
      setSelectedBackup(null);
      setError(null); // エラーをクリア
    } else {
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '32px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{
          margin: '0 0 24px 0',
          fontSize: '20px',
          fontWeight: 'bold',
          color: '#333',
          textAlign: 'center'
        }}>
          {selectedBackup ? uiText("Backup details") : uiText("Backup history")}
        </h2>

        {/* エラー表示 */}
        {error && (
          <div style={{
            padding: '16px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '6px',
            color: '#c33',
            fontSize: '14px',
            marginBottom: '20px',
            lineHeight: '1.5',
            whiteSpace: 'pre-line'
          }}>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '8px',
              fontSize: '15px'
            }}>
              {uiText("❌ An error occurred")}
            </div>
            {error}
          </div>
        )}

        {/* 管理者権限の説明（非管理者の場合） */}
        {!isAdmin && (
          <div style={{
            padding: '12px',
            border: '1px solid var(--workspace-primary, #cfae91)',
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            <div style={{
              fontSize: '14px',
              color: 'var(--workspace-primary, #cfae91)',
              lineHeight: '1.4'
            }}>
              {uiText("Backup restore is available to admins only.")}<br/>
              {uiText("Ask an admin to restore this.")}
            </div>
          </div>
        )}

        {/* 選択されたバックアップの詳細表示 */}
        {selectedBackup ? (
          <div style={{ marginBottom: '24px' }}>
            {/* バックアップ詳細情報 */}
            <div style={{
              padding: '16px',
              border: '2px solid var(--workspace-primary, #cfae91)',
              borderRadius: '6px',
              backgroundColor: '#f9f7f4',
              marginBottom: '20px'
            }}>
              <div style={{
                fontWeight: 'bold',
                fontSize: '16px',
                color: '#333',
                marginBottom: '12px'
              }}>
                {uiText("Backup ID:")} {selectedBackup.id}
              </div>
              <div style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '8px'
              }}>
                {uiText("Created at:")} {formatDate(selectedBackup.backupDate)}
              </div>
              {selectedBackup.createdBy && (
                <div style={{
                  fontSize: '14px',
                  color: '#666',
                  marginBottom: '8px'
                }}>
                  {uiText("Created by:")} {selectedBackup.createdBy}
                </div>
              )}
              <div style={{
                fontSize: '14px',
                color: '#666'
              }}>
                {uiText("Status:")} {selectedBackup.status || 'success'}
              </div>
            </div>

            {/* 注意書き */}
            <div style={{
              padding: '12px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#856404',
                fontWeight: 'bold',
                marginBottom: '4px'
              }}>
                {uiText("Restore notes")}
              </div>
              <div style={{
                fontSize: '13px',
                color: '#856404',
                lineHeight: '1.4'
              }}>
                {uiText("• This will overwrite current data")}<br/>
                {uiText("• Data created after the restore point will not remain")}<br/>
                {uiText("• This can take a while when there are many pages")}<br/>
                {uiText("• A new backup is saved when you run this")}<br/>
                {uiText("• Do not close the browser while restoring")}
              </div>
            </div>

            {/* 進捗表示 */}
            {isRestoring && (
              <div style={{
                marginBottom: '20px',
                padding: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ 
                    fontSize: '14px', 
                    color: '#333',
                    fontWeight: '500'
                  }}>
                    {restoreStatus || uiText("Restoring...")}
                  </span>
                  <span style={{ 
                    fontSize: '14px', 
                    color: '#666',
                    fontWeight: '500'
                  }}>
                    {Math.round(restoreProgress)}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e2e8f0',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${restoreProgress}%`,
                    height: '100%',
                    backgroundColor: 'var(--workspace-primary, #cfae91)',
                    transition: 'width 0.3s ease',
                    borderRadius: '4px'
                  }} />
                </div>
                {restoreProgress === 100 && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '13px',
                    color: '#28a745',
                    fontWeight: '500'
                  }}>
                    {uiText("Restore finished")}
                  </div>
                )}
              </div>
            )}

            {/* 復元ボタン（管理者のみ） */}
            {isAdmin ? (
              <button
                onClick={handleRestore}
                disabled={isRestoring}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: isRestoring ? '#ccc' : '#dc3545',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: isRestoring ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!isRestoring) {
                    e.currentTarget.style.backgroundColor = '#c82333';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isRestoring) {
                    e.currentTarget.style.backgroundColor = '#dc3545';
                  }
                }}
              >
                {isRestoring ? uiText("Restoring...") : uiText("Restore all items")}
              </button>
            ) : (
              <div style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                color: '#6c757d',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                {uiText("Only admins can restore backups")}
              </div>
            )}
          </div>
        ) : (
          /* バックアップ一覧表示 */
          (<>
            {/* ローディング表示 */}
            {loading ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#666',
                fontSize: '14px'
              }}>
                {uiText("Loading now...")}
              </div>
            ) : backups.length === 0 ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#666',
                fontSize: '14px'
              }}>
                {uiText("No backup history")}
              </div>
            ) : (
              /* バックアップ一覧 */
              (<div style={{
                maxHeight: '400px',
                overflowY: 'auto',
                marginBottom: '24px'
              }}>
                {backups.map((backup) => (
                  <div
                    key={backup.id}
                    onClick={() => {
                      if (isAdmin) {
                        setSelectedBackup(backup);
                        setError(null); // エラーをクリア
                      }
                    }}
                    style={{
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginBottom: '6px',
                      backgroundColor: isAdmin ? '#f9f9f9' : '#f5f5f5',
                      transition: 'background-color 0.2s',
                      cursor: 'pointer',
                      opacity: isAdmin ? 1 : 0.6
                    }}
                    onMouseEnter={(e) => {
                      if (isAdmin) {
                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isAdmin) {
                        e.currentTarget.style.backgroundColor = '#f9f9f9';
                      } else {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '4px'
                    }}>
                      <div style={{
                        fontWeight: 'bold',
                        fontSize: '13px',
                        color: '#333'
                      }}>
                        {uiText("Backup ID:")} {backup.id}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: '#666',
                        backgroundColor: '#e8e8e8',
                        padding: '2px 6px',
                        borderRadius: '3px'
                      }}>
                        {backup.status || 'success'}
                      </div>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#666'
                    }}>
                      {uiText("Created at:")} {formatDate(backup.backupDate)}
                      {backup.createdBy && (
                        <span style={{ marginLeft: '12px', color: '#999' }}>
                          {uiText("Created by:")} {backup.createdBy}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>)
            )}
          </>)
        )}

        {/* 戻るボタン */}
        <div style={{
          display: 'flex',
          justifyContent: 'center'
        }}>
          <button
            type="button"
            onClick={handleBack}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: 'transparent',
              color: '#666',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            >
              {selectedBackup ? uiText("← Back to list") : uiText("←Back")}
            </button>
        </div>
      </div>
    </div>
  );
};

export default BackupModal;
